import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { WebPageSchema, OrganizationSchema } from '@/components/base/SchemaOrg';
import { aboutContent, contactInfo } from '@/mocks/site';
import { testimonials } from '@/mocks/articles';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [contactMsg, setContactMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setContactStatus('success');
      setContactMsg('您的留言已成功发送，我们会尽快回复！');
      return;
    }

    setContactStatus('submitting');
    setContactMsg('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9spbep4ne17ei0sdlk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed as Record<string, string>)?.message || text;

      if (res.ok && (parsed as Record<string, string>)?.code === 'OK') {
        setContactStatus('success');
        setContactMsg('您的留言已成功发送，我们会尽快回复！');
        form.reset();
      } else if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setContactStatus('error');
        setContactMsg('提交失败，请稍后再试。');
      } else {
        setContactStatus('error');
        setContactMsg(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setContactStatus('error');
      setContactMsg('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 年轻的妈妈 | 品牌故事与联系方式"
        description="了解年轻的妈妈团队——由儿科医生、泌乳顾问、营养师、早教专家组成的专业育儿平台，致力于为年轻妈妈提供科学温暖的育儿指导。"
        keywords="关于我们,年轻的妈妈,育儿团队,品牌故事,联系方式,育儿专家"
        canonicalUrl="https://young-mama.com/about"
      />
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 年轻的妈妈"
        description="了解年轻的妈妈团队——专业育儿平台，致力于为年轻妈妈提供科学温暖的育儿指导。"
        url="https://young-mama.com/about"
      />

      <main className="min-h-screen pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Page Header */}
          <div className="py-10 md:py-14 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
              关于我们
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              {aboutContent.mission}
            </p>
          </div>

          {/* Brand Story */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  {aboutContent.brandStory.title}
                </h2>
                {aboutContent.brandStory.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
              </div>
              <div className="rounded-lg overflow-hidden h-72 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20team%20of%20women%20working%20together%20in%20a%20bright%20minimalist%20creative%20office%20with%20plants%2C%20soft%20natural%20light%2C%20cream%20and%20blush%20pastel%20color%20scheme%2C%20collaborative%20atmosphere%2C%20editorial%20lifestyle%20photography&width=600&height=450&seq=about-brand-story&orientation=landscape"
                  alt="年轻的妈妈团队"
                  title="年轻的妈妈团队 - 专业育儿指导"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="mb-16 bg-secondary-50/50 rounded-lg px-6 py-10 md:px-10 md:py-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              我们的价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutContent.values.map((v, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${v.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-800 text-sm mb-1.5">{v.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Team */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              专家团队
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed text-center max-w-3xl mx-auto mb-10">
              {aboutContent.teamIntro}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { name: '李医生', title: '儿科主治医师', desc: '10年儿科临床经验，擅长新生儿常见疾病诊治与护理指导。' },
                { name: '林老师', title: '国际认证泌乳顾问', desc: 'IBCLC认证，帮助上千位妈妈成功实现母乳喂养。' },
                { name: '陈营养师', title: '注册营养师', desc: '专注母婴营养，为孕期和产后妈妈提供科学饮食方案。' },
                { name: '刘心理咨询师', title: '国家二级心理咨询师', desc: '专注产后心理支持，帮助妈妈度过育儿期的情绪挑战。' },
              ].map((member, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-3-line text-primary-500 text-xl"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-800 text-sm">{member.name}</h3>
                  <p className="text-xs text-primary-500 mb-2">{member.title}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              妈妈们怎么说
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{t.content}</p>
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span className="font-medium text-foreground-700">{t.name}</span>
                    <span>·</span>
                    <span>{t.babyAge}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mb-16 bg-secondary-50/50 rounded-lg px-6 py-10 md:px-10 md:py-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如果你有任何问题或合作意向，欢迎随时联系我们
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">邮箱</p>
                    <p className="text-sm text-foreground-500">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电话</p>
                    <p className="text-sm text-foreground-500">{contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">微信</p>
                    <p className="text-sm text-foreground-500">{contactInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">地址</p>
                    <p className="text-sm text-foreground-500">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">工作时间</p>
                    <p className="text-sm text-foreground-500">{contactInfo.workingHours}</p>
                  </div>
                </div>
              </div>

              <form data-readdy-form onSubmit={handleContactSubmit}>
                <input
                  className="honeypot-field"
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入你的留言，不超过500字"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={contactStatus === 'submitting'}
                    className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {contactStatus === 'submitting' ? '发送中...' : '发送留言'}
                  </button>
                  {contactMsg && (
                    <p className={`text-sm ${contactStatus === 'success' ? 'text-secondary-700' : 'text-primary-600'}`}>
                      {contactMsg}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}